// @flow
import type { AppState } from '../../annotations';
import { createSelector } from 'reselect';
import * as r from 'ramda';
import { isNotNil, dispatch } from 'ramda-adjunct';
import { getFormValues } from 'redux-form';
import { DomainStatus, AvailabilityStatus } from '../../core/constants';
import { isPremium, isPromo } from '../purchase';
import { getLegalMessages } from '../tld/utils';

//@TODO: Move anything here to utils
const getFqdnAvailability = (
  fqdn: string,
  availabilities: {},
  basketFqdns: ?Array<string>
) => {
  if (basketFqdns && r.contains(fqdn, basketFqdns)) {
    return AvailabilityStatus.AVAILABLE;
  }
  return r.pipe(
    r.prop(fqdn),
    r.defaultTo(AvailabilityStatus.UNVERIFIED),
  )(availabilities);
};

export const getDst = r.prop('dst');

export const getDomainFilters = r.pathOr(
  {},
  [ 'form', 'domainFilters', 'values' ]
);

const getFeatures = createSelector(
  getDst,
  r.prop('features'),
);

const getTransferableTlds = (state, args) => r.when(
  r.is(Array),
  r.reject(r.startsWith('!')), //strip out transferable tlds starting with !
)(r.prop('transferableTlds', args));

const getNonTransferableTlds = (state, args) => r.when(
  r.is(Array),
  r.pipe(
    r.filter(r.startsWith('!')),
    r.map(r.tail),
  ),
)(r.prop('transferableTlds', args));

const getBackorderableTlds = (state, args) => r.prop('backorderableTlds', args);

const getNegotiableTlds = (state, args) => r.prop('negotiableTlds', args);

const getBasketFqdns = (state, args) => r.prop('basketFqdns', args);

const getDisplayPricing = r.pipe(
  r.nthArg(1),
  r.pathOr(12, [ 'displayPricing' ]),
  r.divide(12),
);

const getLegalFunction = (state, { legal }) => legal;

const isExactMatchPromo = (exactMatch) => {
  if (exactMatch.is_purchasable === true && exactMatch.price) {
    if (exactMatch.oldPrice && exactMatch.price < exactMatch.oldPrice) {
      return true;
    }
  }

  return false;
};

const normalizePrice = (field, displayPricing) => r.converge(
  r.assoc(field),
  [
    r.pipe(
      dispatch([
        r.path([ field, 'display' ]),
        r.prop(field),
        r.always(null),
      ]),
      r.when(
        r.is(Number),
        r.divide(r.__, displayPricing),
      ),
    ),
    r.identity,
  ],
);
const normalizePrices = (displayPricing) => r.pipe(
  normalizePrice('price', displayPricing),
  normalizePrice('oldPrice', displayPricing),
);

// $FlowFixMe
export const getExactMatch = createSelector(
  getDst,
  getFeatures,
  getTransferableTlds,
  getNonTransferableTlds,
  getBackorderableTlds,
  getNegotiableTlds,
  getBasketFqdns,
  getLegalFunction,
  getDisplayPricing,
  (
    dst,
    features,
    transferableTlds,
    nonTransferableTlds,
    backorderableTlds,
    negotiableTlds,
    basketFqdns,
    legal,
    displayPricing,
  ) => {
    const format = r.pipe(
      r.path([ 'results', 'exactDomain' ]),
      r.unless(
        r.isNil,
        r.pipe(
          (x) => r.assoc(
            'availability',
            getFqdnAvailability(x.fqdn, dst.availabilities, basketFqdns),
            x,
          ),
          (x) => r.assoc(
            'promo',
            isExactMatchPromo(x),
            x,
          ),
          (x) => r.assoc(
            'legalMessages',
            getLegalMessages(
              legal,
              {
                ...x,
                // eslint-disable-next-line camelcase
                is_ad: false,
              },
            ),
            x,
          ),
          // Can transfer
          (x) => r.assoc(
            'canTransfer',
            r.and(
              //if transfer is true, then check both transferable & nontransferable tld lists
              r.pathEq([ 'transfer' ], true, features),
              r.and(
                //if * is in the transfer list or the extension is in the transferable list, domain is transferable
                r.ifElse(
                  r.is(Array),
                  r.either(
                    r.contains(x.extension),
                    r.contains('*'),
                  ),
                  r.T,
                )(transferableTlds),
                //if the extension is in the nontransferable list, domain is nontransferable
                r.ifElse(
                  r.is(Array),
                  r.complement(r.contains(x.extension)),
                  r.T,
                )(nonTransferableTlds),
              )
            ),
            x,
          ),
          // Can backorder
          (x) => r.assoc(
            'canBackorder',
            r.and(
              r.pathEq([ 'backorder' ], true, features),
              r.ifElse(
                r.is(Array),
                r.contains(x.extension),
                r.F,
              )(backorderableTlds),
            ),
            x,
          ),
          // Can negotiate
          (x) => r.assoc(
            'canNegotiate',
            r.and(
              r.pathEq([ 'negotiate' ], true, features),
              r.cond([
                [
                  r.is(Array),
                  r.contains(x.extension),
                ],
                [
                  r.equals(true),
                  r.T,
                ],
                [
                  r.T,
                  r.F,
                ],
              ])(negotiableTlds),
            ),
            x,
          ),
          // Prices
          normalizePrices(displayPricing),
          // Bundles
          r.when(
            r.has('bundles'),
            r.over(
              r.lensProp('bundles'),
              r.map(normalizePrices(displayPricing)),
            ),
          ),
        )
      ),
    );

    return format(dst);
  },
);

export const getDomainStatus = createSelector(
  getExactMatch,
  (match) => {
    if (match == null) {
      return DomainStatus.VOID;
    }

    if (
      match.is_purchasable === true &&
      r.has('price')(match) &&
      isNotNil(match.price)
    ) {
      if (match.oldPrice && match.price < match.oldPrice) {
        return DomainStatus.PROMO;
      }

      return DomainStatus.AVAILABLE;
    }

    if (
      match.is_purchasable === false &&
      r.has('price')(match) &&
      isNotNil(match.price)
    ) {
      return DomainStatus.TAKEN;
    }

    return DomainStatus.VOID;
  },
);

export const getRawResults = r.pipe(
  getDst,
  r.prop('results'),
);

export const getRawExact = r.pipe(
  getRawResults,
  r.prop('exactDomain'),
);

export const getRawSuggestions = r.pipe(
  getRawResults,
  r.prop('domains'),
);

export const getUnspunResults = (
  state: AppState,
  unspunTlds: Array<string> = [],
) => {
  const { results } = getDst(state);
  const { unspun } = (getFormValues('domainFilters')(state) || {});

  if (results == null || !unspun) {
    return [];
  }

  const query: string = r.pipe(
    getFormValues('domainSearch'),
    r.propOr('', 'query'),
    r.split('.'),
    r.head,
    r.defaultTo(''),
  )(state);

  const usedTlds = r.pipe(
    r.prop('domains'),
    r.append(r.prop('exactDomain', results)),
    r.reject(r.isNil),
    r.map(r.prop('extension')),
  )(results);

  const unusedTlds = r.pipe(
    r.map((tld) => tld.substr(1)),
    // $FlowFixMe
    r.reject(r.contains(r.__, usedTlds)),
    r.map((tld) => ({
      fqdn: `${query}.${tld}`,
      price: null,
      oldPrice: null,
      // eslint-disable-next-line camelcase
      domain_source: '',
      availability: AvailabilityStatus.UNAVAILABLE,
      ad: false,
      premium: false,
      promo: false,
      legalMessages: '',
      unspun: true,
    })),
  )(unspunTlds);

  return unusedTlds;
};

export const getSearchResults = (
  state: AppState,
  args: Object) => {
  const {
    results,
    availabilities,
  } = getDst(state);
  const {
    basketFqdns,
    legal,
    unspunTlds = [],
  } = args;
  const displayPricing = getDisplayPricing(state, args);

  if (results == null) {
    return [];
  }

  const domains = r.prop('domains', results);
  const unusedTlds = getUnspunResults(state, unspunTlds);

  return r.pipe(
    r.map((domain) => {
      const promo = isPromo(domain);
      const { price, oldPrice } = normalizePrices(displayPricing)(domain);

      return {
        fqdn: domain.fqdn,
        ...normalizePrices(displayPricing)(domain),
        price,
        oldPrice,
        // eslint-disable-next-line camelcase
        domain_source: domain.domain_source,
        availability: getFqdnAvailability(
          domain.fqdn,
          availabilities,
          basketFqdns
        ),
        ad: domain.is_ad,
        premium: isPremium(domain),
        promo: promo,
        legalMessages: getLegalMessages(
          legal,
          domain,
        ),
      };
    }),
    // $FlowFixMe
    r.concat(r.__, unusedTlds),
    r.uniqBy(r.prop('fqdn')),
  )(domains);
};

export const getBrowserSession = r.pipe(
  getDst,
  r.prop('browserSession'),
);

export const hasErrored = createSelector(
  getDst,
  r.pipe(
    r.prop('error'),
    isNotNil,
  ),
);

export const hasSearched = createSelector(
  getDst,
  ({
    results,
  }) => {
    return results != null;
  }
);


export const isSeeded = r.pipe(
  getDst,
  r.prop('seeded'),
);
export const isLocaleLoaded = r.pipe(
  getDst,
  r.prop('localeLoaded'),
);
export const getUserQuery = r.pipe(
  getDst,
  r.prop('query'),
);
export const isSearching = r.pipe(
  getDst,
  r.prop('searching'),
);
export const isFiltering = r.pipe(
  getDst,
  r.prop('filtering'),
);
export const getSearchError = r.pipe(
  getDst,
  r.prop('error'),
);

export const getBundleConfig = createSelector(
  getDst,
  getFeatures,
  (dst, features) => {
    const enabled = r.propOr(false, 'bundles', features);
    const options = r.prop('bundle', dst);
    return r.pipe(
      r.assoc('enable', enabled),
      r.merge({
        maxSize: 3,
        minSize: 2,
        excludeTlds: [],
      }),
      r.pick([
        'enable',
        'includeAdSlots',
        'minSize',
        'maxSize',
        'excludeTlds',
      ]),
    )(options);
  },
);

export const getBundleAvailability = r.pipe(
  getDst,
  r.prop('bundleStatus'),
);

export const useAsyncHooks = r.pipe(
  getDst,
  r.prop('asyncHooks'),
);

export const usePPE = r.pipe(
  getDst,
  r.prop('usePPE'),
);

export const includeTax = r.pipe(
  getDst,
  r.prop('includeTax'),
);

export const getAverageTaxRate = createSelector(
  getRawSuggestions,
  r.pipe(
    r.defaultTo([]),
    r.map(
      // TODO: use the tax rate provided by the API
      r.converge(
        r.divide,
        [
          r.path([ 'price', 'tax' ]),
          r.path([ 'price', 'net' ]),
        ],
      ),
    ),
    r.reduce(r.max, 0),
    r.multiply(100),
    Math.round,
  ),
);

export const isAfternicInBaket = createSelector(
  getDst,
  r.prop('afternicInBasket'),
);

export const includeExactInBundle = createSelector(
  getDst,
  r.pathOr(false, [ 'bundle', 'includeExact' ]),
);
